<template>
	<nav
		v-if="totalPages > 1"
		class="category-pagination flex items-center justify-center gap-4 md:gap-6 w-full mt-8 text-sm"
		aria-label="Pagination"
	>
		<NuxtLink
			v-if="currentPage > 1"
			:to="pageLink(currentPage - 1)"
			class="min-w-8 h-11 flex items-center justify-center text-primary"
			:aria-label="$t('Previous page')"
			:aria-current="undefined"
			@click="scrollUp"
		>
			<ChevronLeftIcon class="h-5 w-5" />
		</NuxtLink>
		<span
			v-else
			class="min-w-8 h-11 flex items-center justify-center text-gray-300"
			aria-hidden="true"
		>
			<ChevronLeftIcon class="h-5 w-5" />
		</span>

		<template v-for="(page, index) in pages" :key="`${page}-${index}`">
			<NuxtLink
				v-if="page !== GAP"
				:class="[
					'min-w-8 h-11 flex items-center justify-center transition-colors',
					page === currentPage
						? 'text-primary font-medium border-b-2 border-primary pointer-events-none'
						: 'text-gray-500 hover:text-primary'
				]"
				:to="pageLink(page as number)"
				:aria-label="`${$t('Page')} ${page}`"
				:aria-current="page === currentPage ? 'page' : undefined"
				@click="scrollUp"
			>
				{{ page }}
			</NuxtLink>
			<span
				v-else
				class="min-w-8 h-11 flex items-center justify-center text-gray-400"
				aria-hidden="true"
			>
				...
			</span>
		</template>

		<NuxtLink
			v-if="currentPage < totalPages"
			:to="pageLink(currentPage + 1)"
			class="min-w-8 h-11 flex items-center justify-center text-primary"
			:aria-label="$t('Next page')"
			:aria-current="undefined"
			@click="scrollUp"
		>
			<ChevronRightIcon class="h-5 w-5" />
		</NuxtLink>
		<span
			v-else
			class="min-w-8 h-11 flex items-center justify-center text-gray-300"
			aria-hidden="true"
		>
			<ChevronRightIcon class="h-5 w-5" />
		</span>
	</nav>
</template>

<script setup lang="ts">
// `category-pagination` is a styling hook (styled in promix).
import { computed } from "vue";
import { useRoute } from "vue-router";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";
import { pageQuery } from "~/utils/categories/resolveListingPage";

interface Props {
	currentPage: number;
	itemCount: number;
	pageSize: string;
}

const { currentPage, itemCount, pageSize } = defineProps<Props>();

const route = useRoute();

const GAP = "gap";
// Edge windows are 5 pages wide; the middle window is current ±1 (NF-280 spec).
const EDGE_WINDOW = 5;

const totalPages = computed(() => {
	const pageSizeNum = parseInt(pageSize, 10);
	// Empty/zero page size means "fetch all" — paging disabled.
	if (!pageSizeNum || pageSizeNum <= 0) return 1;
	return Math.max(1, Math.ceil(itemCount / pageSizeNum));
});

/**
 * Windowing per NF-280 spec:
 * - near the start: the first 5 pages, gap, last page
 * - near the end: first page, gap, the last 5 pages
 * - in between: first page, gap, current ±1, gap, last page
 *   (e.g. page 10 of 400 → 1 … 9 10 11 … 400)
 * The edge windows apply only while the NEIGHBOURING page is still inside
 * them — on page 5 the strip is 1 … 4 5 6 … 400 so page 6 stays reachable
 * as a number, not only via the next arrow.
 * Small totals collapse to a plain 1..total strip (no gaps).
 */
const pages = computed<(number | string)[]>(() => {
	const total = totalPages.value;
	const current = Math.min(Math.max(1, currentPage), total);

	let windowStart: number;
	let windowSize: number;
	if (current < EDGE_WINDOW) {
		windowStart = 1;
		windowSize = EDGE_WINDOW;
	} else if (current > total - EDGE_WINDOW + 1) {
		windowStart = total - EDGE_WINDOW + 1;
		windowSize = EDGE_WINDOW;
	} else {
		windowStart = current - 1;
		windowSize = 3;
	}

	const numbers = new Set<number>([1, total]);
	for (let i = 0; i < windowSize; i++) {
		const page = windowStart + i;
		if (page >= 1 && page <= total) numbers.add(page);
	}

	const sorted = [...numbers].sort((a, b) => a - b);
	const result: (number | string)[] = [];
	sorted.forEach((page, index) => {
		if (index > 0 && page - (sorted[index - 1] as number) > 1) {
			result.push(GAP);
		}
		result.push(page);
	});
	return result;
});

// Crawlable links: `?p` carries the page, filter/sort params are preserved.
const pageLink = (page: number) => ({
	path: route.path,
	query: pageQuery(route.query, page)
});

const scrollUp = () => {
	if (typeof window !== "undefined") {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}
};
</script>
